import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { authFetch, authFetchForm } from "../../api";
import { FiTrash2, FiPlus, FiEdit2, FiX } from "react-icons/fi";

const inputClass = "w-full px-3 py-1.5 rounded-md bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light";

const imageFields = [
  { key: "image_1", label: "Image 1 (front)" },
  { key: "image_2", label: "Image 2 (middle)" },
  { key: "image_3", label: "Image 3 (back)" },
];
const allImageFields = [...imageFields, { key: "logo", label: "Logo" }];

const emptyForm = {
  label: "", heading: "", paragraph1: "", paragraph2: "", services: "",
  button_text: "", button_url: "", order: 0, is_active: true,
};

export default function SystemsSettings() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null); // null = add mode, else editing that system's id
  const [form, setForm] = useState(emptyForm);
  const [existingImages, setExistingImages] = useState({}); // current saved image URLs, when editing
  const [formImageFiles, setFormImageFiles] = useState({});
  const [formKey, setFormKey] = useState(0);

  const load = () => {
    authFetch("/core/systems/")
      .then((res) => res.json())
      .then((data) => setItems(data.sort((a, b) => a.order - b.order)))
      .catch(() => setError("Failed to load systems."));
  };

  useEffect(load, []);

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      label: item.label, heading: item.heading, paragraph1: item.paragraph1, paragraph2: item.paragraph2,
      services: item.services, button_text: item.button_text, button_url: item.button_url,
      order: item.order, is_active: item.is_active,
    });
    setExistingImages({ image_1: item.image_1, image_2: item.image_2, image_3: item.image_3, logo: item.logo });
    setFormImageFiles({});
    setFormKey((k) => k + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
    setExistingImages({});
    setFormImageFiles({});
    setFormKey((k) => k + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.heading) return;
    setError("");

    const fd = new FormData();
    fd.append("label", form.label);
    fd.append("heading", form.heading);
    fd.append("paragraph1", form.paragraph1);
    fd.append("paragraph2", form.paragraph2);
    fd.append("services", form.services);
    fd.append("button_text", form.button_text);
    fd.append("button_url", form.button_url);
    fd.append("order", editingId ? form.order : items.length + 1);
    fd.append("is_active", form.is_active);
    allImageFields.forEach(({ key }) => {
      if (formImageFiles[key]) fd.append(key, formImageFiles[key]);
    });

    const url = editingId ? `/core/systems/${editingId}/` : "/core/systems/";
    const res = await authFetchForm(url, fd, editingId ? "PATCH" : "POST");
    if (!res.ok) {
      setError(editingId ? "Failed to update system." : "Failed to add system.");
      return;
    }
    cancelEdit();
    load();
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this system?")) return;
    await authFetch(`/core/systems/${id}/`, { method: "DELETE" });
    if (editingId === id) cancelEdit();
    load();
  };

  return (
    <div className="w-full">
      <h1 className="text-2xl font-bold mb-2">Systems</h1>
      <p className="text-sm text-gray-400 mb-6">
        Manage the "Hire Us As..." system sections shown on the Home page (e.g. ERP Accounting, EMI Accounting). Add
        as many as you need — odd position shows the image on the left, even position shows it on the right.
      </p>

      {error && <p className="text-red-400 mb-4">{error}</p>}

      <form key={formKey} onSubmit={handleSubmit} className="mb-6 bg-navy border border-navy-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{editingId ? "Edit System" : "Add New System"}</h2>
          {editingId && (
            <button type="button" onClick={cancelEdit} className="flex items-center gap-1 text-xs text-gray-400 hover:text-white">
              <FiX size={14} /> Cancel edit
            </button>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Label</label>
            <input
              className={inputClass}
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. POS System"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Heading</label>
            <input
              className={inputClass}
              value={form.heading}
              onChange={(e) => setForm({ ...form, heading: e.target.value })}
              placeholder="e.g. Hire Us As POS Accountant"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs text-gray-400 mb-1">Paragraph 1</label>
          <div className="bg-white text-black rounded-md">
            <ReactQuill
              theme="snow"
              value={form.paragraph1}
              onChange={(value) => setForm({ ...form, paragraph1: value })}
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs text-gray-400 mb-1">
            Paragraph 2 (select text and click Bold to highlight it in accent color)
          </label>
          <div className="bg-white text-black rounded-md">
            <ReactQuill
              theme="snow"
              value={form.paragraph2}
              onChange={(value) => setForm({ ...form, paragraph2: value })}
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs text-gray-400 mb-1">Service Tags (one per line)</label>
          <textarea
            className={`${inputClass} min-h-[140px] resize-y`}
            value={form.services}
            onChange={(e) => setForm({ ...form, services: e.target.value })}
            placeholder={"Enquiry Handling\nInvoice Generation\n..."}
          />
        </div>

        <div className="grid sm:grid-cols-4 gap-4 mt-4">
          {allImageFields.map(({ key, label }) => {
            const previewSrc = formImageFiles[key] ? URL.createObjectURL(formImageFiles[key]) : existingImages[key];
            return (
              <div key={key}>
                <p className="text-sm text-gray-300 mb-2">{label}</p>
                {previewSrc && (
                  <img src={previewSrc} alt={label} className="w-full h-24 object-cover rounded-md border border-navy-border mb-2" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormImageFiles((prev) => ({ ...prev, [key]: e.target.files[0] }))}
                  className="text-xs text-gray-400"
                />
              </div>
            );
          })}
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Button Text</label>
            <input
              className={inputClass}
              value={form.button_text}
              onChange={(e) => setForm({ ...form, button_text: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Button URL</label>
            <input
              type="url"
              className={inputClass}
              value={form.button_url}
              onChange={(e) => setForm({ ...form, button_url: e.target.value })}
              placeholder="https://your-system.com"
            />
          </div>
        </div>

        {editingId && (
          <div className="mt-4">
            <label className="block text-xs text-gray-400 mb-1">Order</label>
            <input
              type="number"
              className={`${inputClass} max-w-[120px]`}
              value={form.order}
              onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
            />
          </div>
        )}

        <div className="flex items-center gap-6 mt-4">
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>
          <button className="ml-auto px-4 py-2 rounded-md bg-brand-gradient text-white text-sm font-semibold flex items-center gap-2">
            {editingId ? (<>Save Changes</>) : (<><FiPlus /> Add System</>)}
          </button>
        </div>
      </form>

      <div className="bg-navy border border-navy-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-gray-400 text-left">
            <tr>
              <th className="px-4 py-3 w-24">Image</th>
              <th className="px-4 py-3">Heading</th>
              <th className="px-4 py-3">Label</th>
              <th className="px-4 py-3 w-20">Order</th>
              <th className="px-4 py-3 w-20">Active</th>
              <th className="px-4 py-3 w-28"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t border-navy-border align-middle">
                <td className="px-4 py-3">
                  {item.image_1 ? (
                    <img src={item.image_1} alt={item.heading} className="w-16 h-12 object-cover rounded-md border border-navy-border" />
                  ) : (
                    <div className="w-16 h-12 rounded-md bg-white/5 border border-navy-border" />
                  )}
                </td>
                <td className="px-4 py-3 text-white">{item.heading}</td>
                <td className="px-4 py-3 text-gray-400">{item.label}</td>
                <td className="px-4 py-3 text-gray-400">{item.order}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs ${item.is_active ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-gray-500"}`}>
                    {item.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => startEdit(item)}
                      className="p-1.5 rounded-md text-accent-light hover:bg-accent-light/10 flex items-center gap-1 text-xs"
                    >
                      <FiEdit2 size={14} /> Edit
                    </button>
                    <button
                      onClick={() => deleteItem(item.id)}
                      className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10 flex items-center gap-1 text-xs"
                    >
                      <FiTrash2 size={14} /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-500">No systems yet — add one above.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
